import React, { useEffect, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
} from "@store/slices/chart-config-slice";
import {
  resolveStackManagedItems,
  resolveSegmentManagedItems,
  getStackedVisibleCounts,
  reconcileStackedChartData,
  stackedChartDataEquals,
} from "@utils/stacked-bar-utils";
import { useStackedBarVariables } from "../hooks/useStackedBarVariables";
import StackedManagedList from "./StackedManagedList";
import StackedSelectionSummary from "./StackedSelectionSummary";

interface StackedCategorySectionProps {
  isDisabled: boolean;
}

/**
 * Category presentation for stacked bar: the "Stacks" (Grid Variables) and
 * "Segments" (Grid Values) management lists plus a summary.
 */
const StackedCategorySection: React.FC<StackedCategorySectionProps> = ({
  isDisabled,
}) => {
  const dispatch = useAppDispatch();
  const { currentConfig } = useAppSelector(chartConfigSelector);
  const { variables, values } = useStackedBarVariables();

  const {
    toggleStackedStackVisibility,
    setStackedStackVisibility,
    setStackedStackLabelOverride,
    reorderStackedStacks,
    setStackedSegmentLabelOverride,
    reorderStackedSegments,
    setStackedChartData,
  } = chartConfigDispatcher(dispatch);

  const stackedBar = currentConfig.stackedBar;

  // Reconcile the fetched chart data ids to the datasetElementIds so
  // visibility / overrides / order match. The response uses different ids
  // than the universe; matching is done by label.
  useEffect(() => {
    const chartData = stackedBar?.chartData;
    if (!chartData) return;
    if (variables.length === 0 && values.length === 0) return;
    const reconciled = reconcileStackedChartData(chartData, variables, values);

    const keyOf = (c: { sourceLabel?: string; label: string }) =>
      c.sourceLabel ?? c.label;
    const previousKeys = new Set(chartData.categories.map(keyOf));
    const newlyDiscoveredIds = reconciled.categories
      .filter(
        (c) =>
          !previousKeys.has(keyOf(c)) &&
          stackedBar?.stackCustomizations[c.id] === undefined,
      )
      .map((c) => c.id);

    if (!stackedChartDataEquals(chartData, reconciled)) {
      setStackedChartData(reconciled);
    }
    newlyDiscoveredIds.forEach((id) => {
      setStackedStackVisibility({ id, visible: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackedBar?.chartData, variables, values]);

  const stackItems = useMemo(
    () =>
      resolveStackManagedItems(
        stackedBar?.chartData?.categories ?? [],
        stackedBar?.stackOrder ?? [],
        stackedBar?.stackCustomizations ?? {},
      ),
    [stackedBar],
  );
  const segmentItems = useMemo(
    () =>
      resolveSegmentManagedItems(
        stackedBar?.chartData?.series ?? [],
        stackedBar?.segmentOrder ?? [],
        stackedBar?.segmentCustomizations ?? {},
      ),
    [stackedBar],
  );

  const { stacks, segments } = getStackedVisibleCounts(stackedBar);
  const warning = stacks < 1 ? "Show at least one stack." : undefined;

  return (
    <Box
      sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Categories
      </Typography>

      <StackedManagedList
        title="Stacks"
        items={stackItems}
        isDisabled={isDisabled}
        helperText="Categories shown along the axis."
        showVisibilityToggle
        onToggleVisibility={(id) => toggleStackedStackVisibility({ id })}
        onReorder={(order) => reorderStackedStacks(order)}
        onLabelChange={(id, label) =>
          setStackedStackLabelOverride({ id, label })
        }
        onResetLabel={(id) => setStackedStackLabelOverride({ id, label: "" })}
      />

      <StackedManagedList
        title="Segments"
        items={segmentItems}
        isDisabled={isDisabled}
        helperText="Response values stacked within each Stack."
        showVisibilityToggle={false}
        onToggleVisibility={() => {}}
        onReorder={(order) => reorderStackedSegments(order)}
        onLabelChange={(id, label) =>
          setStackedSegmentLabelOverride({ id, label })
        }
        onResetLabel={(id) =>
          setStackedSegmentLabelOverride({ id, label: "" })
        }
      />

      <Divider />

      <StackedSelectionSummary
        stacks={stacks}
        segments={segments}
        warning={warning}
      />
    </Box>
  );
};

export default StackedCategorySection;
